import { Grid } from '@methanesat/ui-components';
import { ErrorBoundary } from 'react-error-boundary';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    DrawerWithCardsProps,
    isBasinInfo,
    isClusteredPointInfrastructureInfo,
    isPipelineInfo,
    isPointInfrastructureInfo,
    OGIInfo
} from '../../../../types';
import { DrawerWithCards } from './DrawerWithCards';
import { BasinCard, ClusteredInfrastructureCard, PipelineCard, PointInfrastructureCard } from '../DataSpecific';
import { useTranslate } from '../../../../hooks';
import { addOneMapFeatureHighlight, setSelectedOGIFeature } from '../../../../reducers';
import { createHighlightPayload } from '../../../../utils';

type HiddenCard = string | number;

export const OGIDrawerWithCards = ({
    activeCardIndex,
    cardData,
    onAccordionChange,
    closeDrawer,
    ...otherProps
}: Omit<DrawerWithCardsProps, 'children'> & {
    cardData: OGIInfo[];
    onAccordionChange: (newExpandedAccordion: string, cardIndex: number) => void;
    closeDrawer: () => void;
}) => {
    const dispatch = useDispatch();
    const t = useTranslate();
    const [cardList, setCardList] = useState(cardData);
    const [hiddenCard, setHiddenCard] = useState<HiddenCard[]>([]);

    // if all cards are hidden, close the drawer
    useEffect(() => {
        if (cardList.length === 0) {
            closeDrawer();
        }
    }, [cardList, closeDrawer]);

    // ensure that drawer data updates if clicking another OGI feature/cluster
    useEffect(() => {
        setCardList(cardData);
    }, [cardData]);

    // add a card to the hidden list
    const filterCards = (id: string | number) => {
        setHiddenCard((prev) => {
            if (prev.includes(id)) return prev;
            return [...prev, id];
        });
    };

    // if the hiddenCard list updates, filter the cards based on the updated list and change the highlight to the 0 index card
    useEffect(() => {
        const filteredCardList = cardData.filter((card) => {
            return !hiddenCard.includes(card.object.id);
        });

        if (filteredCardList.length) {
            dispatch(addOneMapFeatureHighlight(createHighlightPayload(filteredCardList[0])));
            dispatch(setSelectedOGIFeature(filteredCardList[0].object));
        }

        setCardList(filteredCardList);
    }, [hiddenCard, cardData, dispatch]);

    return (
        <DrawerWithCards
            {...otherProps}
            activeCardIndex={activeCardIndex}
            title={
                cardList.length === 1
                    ? t('emissionsMapPage.drawerWithCards.ogi.titleSingular', {
                          count: cardList.length
                      })
                    : t('emissionsMapPage.drawerWithCards.ogi.titlePlural', {
                          count: cardList.length
                      })
            }
            data-testid="ogi-drawer-with-cards"
        >
            <Grid container rowSpacing={3}>
                {cardList.map((info, idx) => {
                    return (
                        <Grid size={{ xs: 12 }} key={idx}>
                            <ErrorBoundary fallback={<div>{t('emissionsMapPage.error.ogiData')}</div>}>
                                {isBasinInfo(info) && <BasinCard info={info} />}
                                {isClusteredPointInfrastructureInfo(info) && (
                                    <ClusteredInfrastructureCard
                                        count={info.object.properties.point_count}
                                        type={info.object.properties.layerName}
                                    />
                                )}
                                {isPipelineInfo(info) && (
                                    <PipelineCard
                                        info={info}
                                        accordion={cardData.length > 1}
                                        cardIndex={idx}
                                        expanded={activeCardIndex === idx}
                                        onAccordionChange={onAccordionChange}
                                        filterCards={filterCards}
                                    />
                                )}
                                {!isClusteredPointInfrastructureInfo(info) && isPointInfrastructureInfo(info) && (
                                    <PointInfrastructureCard
                                        info={info}
                                        accordion={cardData.length > 1}
                                        cardIndex={idx}
                                        expanded={activeCardIndex === idx}
                                        onAccordionChange={onAccordionChange}
                                        filterCards={filterCards}
                                    />
                                )}
                            </ErrorBoundary>
                        </Grid>
                    );
                })}
            </Grid>
        </DrawerWithCards>
    );
};
